import { AMOUNT_QUOTE_DEBOUNCE_MS } from "../../utils/amountQuote";
import { useEffect, useRef, useState } from "react";
import {
  maxSolanaBatchPayout,
  quoteSolanaWithdrawal,
  type SolanaQuoteContext,
} from "../../joinsplit-v2/solana/withdrawalQuote";
import type { SolanaWithdrawalQuote } from "../../joinsplit-v2/solana/fees";

/** Quotes are scoped to account, pool, notes, recipients and amount. Stale
 * responses never enable confirmation after the user edits a form. */
export function useSolanaWithdrawalQuote({
  context,
  account,
  amount,
  batch,
  enabled,
}: {
  context: SolanaQuoteContext;
  account: string;
  amount: bigint | null;
  batch: boolean;
  enabled: boolean;
}) {
  const [result, setResult] = useState<{
    key: string;
    quote: SolanaWithdrawalQuote | null;
    error: string | null;
  }>({ key: "", quote: null, error: null });
  const [maximum, setMaximum] = useState<{
    key: string;
    value: bigint;
    loading: boolean;
    error: string | null;
  }>({
    key: "",
    value: 0n,
    loading: false,
    error: null,
  });
  const [refresh, setRefresh] = useState(0);
  const contextKey = JSON.stringify([
    enabled,
    account,
    context.network.key,
    context.network.programId,
    context.mint,
    context.recipients,
    batch,
    context.notes.map(({ note, spent }) => [
      note.commitment,
      note.value.toString(),
      note.treeIndex,
      note.leafIndex,
      spent,
    ]),
  ]);
  const key = `${contextKey}:${amount?.toString() ?? ""}`;
  const latest = useRef(context);
  latest.current = context;
  useEffect(() => {
    setResult({ key, quote: null, error: null });
    if (!enabled || amount === null || amount <= 0n) return;
    const stop = new AbortController();
    const timer = setTimeout(() => {
      void quoteSolanaWithdrawal(
        { ...latest.current, signal: stop.signal },
        amount,
        batch,
      ).then(
        (quote) => {
          if (!stop.signal.aborted) setResult({ key, quote, error: null });
        },
        (error) => {
          if (!stop.signal.aborted)
            setResult({
              key,
              quote: null,
              error:
                error instanceof Error
                  ? error.message
                  : "Solana fee quote unavailable. Try again.",
            });
        },
      );
    }, AMOUNT_QUOTE_DEBOUNCE_MS);
    return () => {
      stop.abort();
      clearTimeout(timer);
    };
  }, [key, enabled, amount, batch, refresh]);

  useEffect(() => {
    if (!enabled) return;
    const retry = setTimeout(() => setRefresh((value) => value + 1), 30_000);
    return () => clearTimeout(retry);
  }, [enabled, key, refresh]);

  useEffect(() => {
    setMaximum({
      key: contextKey,
      value: 0n,
      loading: enabled && batch,
      error: null,
    });
    if (!enabled || !batch) return;
    const stop = new AbortController();
    const timer = setTimeout(() => {
      void maxSolanaBatchPayout({
        ...latest.current,
        signal: stop.signal,
      }).then(
        (value) => {
          if (!stop.signal.aborted)
            setMaximum({ key: contextKey, value, loading: false, error: null });
        },
        (error) => {
          if (!stop.signal.aborted)
            setMaximum({
              key: contextKey,
              value: 0n,
              loading: false,
              error:
                error instanceof Error
                  ? error.message
                  : "MAX is unavailable. Retry the fee quote.",
            });
        },
      );
    }, AMOUNT_QUOTE_DEBOUNCE_MS);
    return () => {
      stop.abort();
      clearTimeout(timer);
    };
  }, [contextKey, enabled, batch, refresh]);
  const quote =
    enabled &&
    result.key === key &&
    result.quote &&
    result.quote.quote.expiresAt > Date.now()
      ? result.quote
      : null;
  return {
    quote,
    error: result.key === key ? result.error : null,
    loading:
      enabled &&
      amount !== null &&
      !quote &&
      !(result.key === key && result.error),
    batchMaxTotal: enabled && maximum.key === contextKey ? maximum.value : 0n,
    batchMaxLoading:
      enabled && batch && (maximum.key !== contextKey || maximum.loading),
    batchMaxError:
      enabled && batch && maximum.key === contextKey ? maximum.error : null,
    refresh: () => setRefresh((value) => value + 1),
  };
}
